import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Marker, Polyline } from '@react-google-maps/api';
import { segmentDirections } from '@constants/component-configs';
import {
  detailEditCircleStyle,
  detailActiveCircleStyle,
  detailEditLineStyle,
  detailActiveLineStyle,
  detailPolyLineStyle
} from '@constants/data-detail';
import {
  getOneDirectionIcon,
  getBothDirectionIcon
} from '@icons/map-icons';
import MapFigure from '@shared/map-figure';
import wrapSymbolPath from '@shared/symbol-path-wrapper';
import { generatePath } from '@utils/map-utils';

const SymbolPathMarker = wrapSymbolPath(Marker);

const lineInteractions = {
  clickable: false,
  draggable: false,
  editable: false
};

const MapEditFigure = ({
  direction,
  end: endProp,
  geometryLocked: geometryLockedProp,
  onChange,
  shape,
  start: startProp
}) => {
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState(startProp);
  const [end, setEnd] = useState(endProp);
  const [geometryLocked] = useState(geometryLockedProp);

  // Sync state when props change
  useEffect(() => {
    if (startProp !== start && !dragging) {
      setStart(startProp);
    }
  }, [startProp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (endProp !== end && !dragging) {
      setEnd(endProp);
    }
  }, [endProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoized styles
  const draggableStyle = useMemo(() => ({
    ...detailEditCircleStyle,
    draggable: true
  }), []);

  const endDraggableStyle = useMemo(() => ({
    ...draggableStyle,
    zIndex: draggableStyle.zIndex * 2
  }), [draggableStyle]);

  const lineStyle = useMemo(() => ({
    ...detailEditLineStyle,
    ...lineInteractions
  }), []);

  const centeredElementStyle = useMemo(() => ({
    ...detailActiveLineStyle,
    ...detailActiveCircleStyle,
    ...lineInteractions,
    ...detailPolyLineStyle
  }), []);

  const handleStartChange = useCallback(({ latLng }) => {
    setStart(latLng.toJSON());
    setDragging(true);
  }, []);

  const handleEndChange = useCallback(({ latLng }) => {
    setEnd(latLng.toJSON());
    setDragging(true);
  }, []);

  const handleStartChanged = useCallback(event => {
    const newStart = event.latLng.toJSON();
    setStart(newStart);
    setDragging(false);
    onChange({ start: newStart, end, geometryLocked, dragging: false });
  }, [end, geometryLocked, onChange]);

  const handleEndChanged = useCallback(event => {
    const newEnd = event.latLng.toJSON();
    setEnd(newEnd);
    setDragging(false);
    onChange({ start, end: newEnd, geometryLocked, dragging: false });
  }, [start, geometryLocked, onChange]);

  const getSelectedLineOptions = useCallback(() => {
    const options = lineStyle;
    if (direction && !dragging) {
      if (direction === segmentDirections.one.value) {
        return {
          ...options,
          icons: [{
            ...options.icons[0],
            icon: getOneDirectionIcon()
          }]
        };
      }
      if (direction === segmentDirections.both.value) {
        return {
          ...options,
          icons: [{
            ...options.icons[0],
            icon: getBothDirectionIcon()
          }]
        };
      }
    }
    return options;
  }, [direction, dragging, lineStyle]);

  const getPolylinePath = useCallback(() => {
    if (shape && shape.coordinates) {
      return generatePath(shape.coordinates);
    }
    return [start, end];
  }, [shape, start, end]);

  const selectedLineOptions = getSelectedLineOptions();

  return (
    <div>
      {shape &&
        <MapFigure shape={shape} options={centeredElementStyle} />
      }
      {start && !geometryLocked &&
        <SymbolPathMarker
          position={start}
          options={draggableStyle}
          onDrag={handleStartChange}
          onDragEnd={handleStartChanged}
        />
      }
      {end && !geometryLocked &&
        <SymbolPathMarker
          position={end}
          options={endDraggableStyle}
          onDrag={handleEndChange}
          onDragEnd={handleEndChanged}
        />
      }
      {start && end &&
        <Polyline
          path={getPolylinePath()}
          options={selectedLineOptions}
        />
      }
    </div>
  );
};

MapEditFigure.propTypes = {
  direction: PropTypes.number,
  end: PropTypes.object,
  geometryLocked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  shape: PropTypes.object,
  start: PropTypes.object
};

export default MapEditFigure;
